import './GfModal.scss';

import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { AdditionalButtonProps, GfModalFocusAction, ModalButtonClickEvent } from './modal-types';
import { Button, Checkbox, Icon, Modal, StrictModalProps } from 'semantic-ui-react';
import { CloseDark } from '../../icons/icons';
import { GfComponentProps } from 'gf-types';

export type GfModalProps = GfComponentProps<{
  /** The title of the modal */
  title?: string;

  /**
   * The primary (right, orange) button text.
   *
   * Leave it `undefined` to not render it.
   */
  primaryText?: string;

  /**
   * The secondary (middle, light blue) button text.
   * Use this button for secondary, non-cancel actions, i.e.: a, "No" button.
   *
   * Leave it `undefined` to not render it.
   */
  secondaryText?: string;

  /**
   * The tertiary (left, borderless blue) button text.
   * Use this button for setting cancelable actions, i.e.: a "Cancel" button.
   *
   * Leave it `undefined` to not render it.
   */
  tertiaryText?: string;

  /**
   * The error text to display by the buttons
   * Leave it `undefined` to not render it.
   */
  errorText?: string;

  /** An informational text that is shown on the left side of the footer. */
  infoText?: string;

  /** Show a loading state on the primary button. */
  loading?: boolean;

  /** True, to allow the content body to scroll. False, otherwise. */
  scrolling?: boolean;

  /**
   * True, to show the modal, false otherwise.
   *
   * Default: `true`
   */
  open?: boolean;

  /**
   * A map of additional button props to apply to the action buttons.
   *
   * @example
   * const buttonProps = {
   *   primary: { disabled: true, className: 'different-primary' },
   *   secondary: { className: 'different-secondary' }
   * };
   *
   * <GfModal actionButtonProps={buttonProps}>...</GfModal>
   */
  actionButtonProps?: AdditionalButtonProps;

  /** A map of additional semantic-ui modal props to directly apply to the underlying modal.
   *
   * @example
   * <GfModal semanticUiModalProps={{ size: 'small', closeOnDocumentClick: true }}>...</GfModal>
   */
  semanticUiModalProps?: StrictModalProps;

  /** Tell the modal which action button should have an optional focus when first opened. */
  focusAction?: GfModalFocusAction;

  /**
   * True, to show the footer regardless if there are actions, false otherwise.
   */
  showFooterWithNoActions?: boolean;

  /** Called when the modal is closing  */
  onClose?: VoidFunction;
  onPrimaryClick?: ModalButtonClickEvent;
  onSecondaryClick?: ModalButtonClickEvent;
  onTertiaryClick?: ModalButtonClickEvent;
}>;

/**
 * A general, basic, modal that can be used to provide confirmation, alerts and prompts to the user.
 *
 * Defaults: `open: true`, `size: 'tiny'`, `dimmer: 'inverted'`, `closeOnDimmerClick: false`
 *
 * The base for this modal is a [semantic-ui modal](https://react.semantic-ui.com/modules/modal).
 */
export default function GfModal(props: GfModalProps) {
  const closeRef = useRef<HTMLButtonElement>();
  const primaryActionRef = useRef<Button>();
  const secondaryActionRef = useRef<Button>();
  const tertiaryActionRef = useRef<Button>();
  const [isChecked, setIsChecked] = useState(false);

  const showActions = props.showFooterWithNoActions || props.primaryText || props.secondaryText || props.tertiaryText;
  const openModal = props.open ?? true;

  useEffect(() => {
    if (!props.focusAction) {
      return;
    }

    switch (props.focusAction) {
      case 'close':
        closeRef.current.focus();
        break;
      case 'primary':
        primaryActionRef.current.focus();
        break;
      case 'secondary':
        secondaryActionRef.current.focus();
        break;
      case 'tertiary':
        tertiaryActionRef.current.focus();
        break;
      default:
        break;
    }
  }, [props.focusAction]);

  return (
    <Modal
      className={classNames(props.className, 'gf-modal')}
      open={openModal}
      size="tiny"
      dimmer="inverted"
      closeOnDimmerClick={false}
      onClose={props.onClose}
      tabIndex={props.tabIndex}
      {...(props.semanticUiModalProps || {})}
    >
      <Modal.Header className={classNames('gf-modal-header', { headerless: !props.title })}>
        {props.title}
        <button ref={closeRef} className="close-button plain-button" onClick={props.onClose} aria-label="Close">
          <CloseDark />
        </button>
      </Modal.Header>

      <Modal.Content className="gf-modal-content" scrolling={props.scrolling}>
        {props.children}
      </Modal.Content>

      {showActions && (
        <Modal.Actions className="gf-modal-actions">
          {props.actionButtonProps?.checkbox && <Checkbox {...props.actionButtonProps.checkbox} onClick={(_e, data) => setIsChecked(data.checked)} />}
          {props.errorText && (
            <div className="gf-modal-error">
              <span>{props.errorText}</span>
            </div>
          )}
          {props.infoText && (
            <div className="gf-modal-info">
              <Icon className="info-icon" circular inverted color="grey" name="info" size="tiny" />
              <span>{props.infoText}</span>
            </div>
          )}
          {props.tertiaryText && (
            <Button
              ref={tertiaryActionRef}
              className="tertiary gf-modal-button"
              onClick={(event) => props.onTertiaryClick?.call(null, event)}
              {...(props.actionButtonProps?.tertiary ?? {})}
            >
              {props.tertiaryText}
            </Button>
          )}
          {props.secondaryText && (
            <Button
              ref={secondaryActionRef}
              className="gf-modal-button"
              secondary
              onClick={(event) => props.onSecondaryClick?.call(null, event)}
              {...(props.actionButtonProps?.secondary ?? {})}
            >
              {props.secondaryText}
            </Button>
          )}
          {props.primaryText && (
            <div data-tooltip={props.actionButtonProps?.primary?.title} data-position="top center" className="gf-tooltip">
              <Button
                ref={primaryActionRef}
                className="gf-modal-button"
                primary
                loading={props.loading}
                onClick={(event) => {
                  if (!props?.loading) {
                    props.onPrimaryClick?.call(null, { ...event, checkboxChecked: isChecked });
                  }
                }}
                {...(props.actionButtonProps?.primary ?? {})}
              >
                {props.primaryText}
              </Button>
            </div>
          )}
        </Modal.Actions>
      )}
    </Modal>
  );
}
